import {
  useLayoutEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { useGuidedMode } from './guidedModeState';
import type {
  FieldHelpContent,
  RegisteredFieldHelp,
  RegisteredRegistry,
} from './fieldHelpRegistryCore';

import './fieldHelp.css';

function idSegment(value: string): string {
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 72);
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16777619);
  }
  return `${normalized || 'field'}-${(hash >>> 0).toString(36)}`;
}

function describedByWithout(control: HTMLElement, helpId: string | null) {
  return (control.getAttribute('aria-describedby') ?? '')
    .split(/\s+/)
    .filter((token) => token.length > 0 && token !== helpId);
}

interface FieldHelpProps {
  content: FieldHelpContent;
  id: string;
}

export function FieldHelp({ content, id }: FieldHelpProps) {
  const optionalDetails = [
    ['Example', content.example],
    ['Units', content.units],
    ['Source', content.source],
    ['Range', content.range],
    ['Validation', content.validation],
    ['Caveat', content.caveat],
  ] as const;

  return (
    <span className="field-help" id={id} role="note">
      <span className="field-help__primary">
        <span>
          <strong>Enter</strong>
          {content.whatToEnter}
        </span>
        <span>
          <strong>Why</strong>
          {content.whyItMatters}
        </span>
      </span>
      {optionalDetails.some(([, value]) => value !== undefined) ? (
        <span className="field-help__details">
          {optionalDetails.map(([label, value]) =>
            value === undefined ? null : (
              <span key={label}>
                <strong>{label}</strong>
                {value}
              </span>
            ),
          )}
        </span>
      ) : null}
    </span>
  );
}

interface HelpSlot {
  content: RegisteredFieldHelp;
  helpId: string;
  host: HTMLSpanElement;
}

interface GuidedFieldProps extends PropsWithChildren {
  registry: RegisteredRegistry;
}

export function GuidedField({ children, registry }: GuidedFieldProps) {
  const { enabled } = useGuidedMode();
  const rootRef = useRef<HTMLDivElement>(null);
  const slotsRef = useRef(new Map<HTMLElement, HelpSlot>());
  const signatureRef = useRef('');
  const [portals, setPortals] = useState<ReactNode[]>([]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (root === null) return undefined;
    const slots = slotsRef.current;
    let queued = false;

    const scan = () => {
      queued = false;
      const controls = Array.from(
        root.querySelectorAll<HTMLElement>(
          'input[data-guided-field], select[data-guided-field], textarea[data-guided-field]',
        ),
      );
      const activeControls = new Set(controls);
      const keyCounts = new Map<string, number>();
      const nextSlots: HelpSlot[] = [];

      for (const [control, slot] of slots) {
        if (!enabled || !activeControls.has(control)) {
          const remaining = describedByWithout(control, slot.helpId);
          if (remaining.length === 0) control.removeAttribute('aria-describedby');
          else control.setAttribute('aria-describedby', remaining.join(' '));
          control.removeAttribute('data-guided-help-id');
          slot.host.remove();
          slots.delete(control);
        }
      }

      if (enabled) {
        controls.forEach((control) => {
          const fieldKey = control.dataset.guidedField ?? '';
          const content = registry[fieldKey];
          if (content === undefined) {
            control.dataset.guidedMissing = 'true';
            return;
          }
          delete control.dataset.guidedMissing;
          const ordinal = (keyCounts.get(fieldKey) ?? 0) + 1;
          keyCounts.set(fieldKey, ordinal);
          const instance =
            control.dataset.guidedInstance ??
            (control.id ||
              control.getAttribute('name') ||
              control.getAttribute('aria-label') ||
              String(ordinal));
          const helpId = `guided-field-help-${idSegment(fieldKey)}-${idSegment(instance)}`;
          let slot = slots.get(control);
          if (slot !== undefined && slot.helpId !== helpId) {
            const remaining = describedByWithout(control, slot.helpId);
            if (remaining.length === 0) control.removeAttribute('aria-describedby');
            else control.setAttribute('aria-describedby', remaining.join(' '));
            slot.host.remove();
            slots.delete(control);
            slot = undefined;
          }
          if (slot === undefined) {
            const host = document.createElement('span');
            host.className = 'field-help-slot';
            host.dataset.guidedHelpSlot = helpId;
            const label = control.closest('label');
            const anchor = label ?? control;
            anchor.insertAdjacentElement('afterend', host);
            slot = { content, helpId, host };
            slots.set(control, slot);
          }
          const describedBy = describedByWithout(
            control,
            control.dataset.guidedHelpId ?? null,
          );
          const composed = [...describedBy, helpId].join(' ');
          if (control.getAttribute('aria-describedby') !== composed) {
            control.setAttribute('aria-describedby', composed);
          }
          control.dataset.guidedHelpId = helpId;
          nextSlots.push(slot);
        });
      }

      const signature = nextSlots.map((slot) => slot.helpId).join('|');
      if (signature !== signatureRef.current) {
        signatureRef.current = signature;
        setPortals(
          nextSlots.map((slot) =>
            createPortal(
              <FieldHelp content={slot.content} id={slot.helpId} />,
              slot.host,
              slot.helpId,
            ),
          ),
        );
      }
    };

    const queueScan = () => {
      if (queued) return;
      queued = true;
      queueMicrotask(scan);
    };
    scan();
    const observer = new MutationObserver(queueScan);
    observer.observe(root, {
      attributeFilter: [
        'aria-describedby',
        'data-guided-field',
        'data-guided-instance',
      ],
      attributes: true,
      childList: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
      for (const [control, slot] of slots) {
        const remaining = describedByWithout(control, slot.helpId);
        if (remaining.length === 0) control.removeAttribute('aria-describedby');
        else control.setAttribute('aria-describedby', remaining.join(' '));
        control.removeAttribute('data-guided-help-id');
        slot.host.remove();
      }
      slots.clear();
      signatureRef.current = '';
    };
  }, [enabled, registry]);

  return (
    <div className="guided-field-scope" ref={rootRef}>
      {children}
      {portals}
    </div>
  );
}
